// ============================================================
//  src/pages/SubscriptionPage.jsx  — RK Farm Foods Subscriptions
//  Design: Luxury Organic Editorial — warm parchment, serif
//  display, rich earthy palette, editorial card layouts
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Toast } from "../components/UI";
import { supabase } from "../utils/supabase";
import config from "../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLeaf, faUsers, faStar, faBox, faClipboardList, faTruck,
  faRepeat, faGear, faQuestionCircle, faWheatAwn, faHouse,
  faSeedling, faBowlFood, faJar, faPlusCircle, faCheckCircle,
  faCheck, faCircleInfo, faArrowRight, faKey, faRotate,
  faShieldHalved, faCommentDots, faChevronDown, faXmark,
  faAward, faHeartPulse, faComments, faFire, faCrown,
} from "@fortawesome/free-solid-svg-icons";

// ── Google Fonts injection ────────────────────────────────────
const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;0,900;1,600;1,700&family=DM+Sans:wght@300;400;500;600;700&display=swap');`;

// ── Design tokens ─────────────────────────────────────────────
const T = {
  forest:       "#1c3a18",
  forestMid:    "#2d5a27",
  leaf:         "#4a8c3f",
  parchment:    "#f5f0e8",
  parchmentDark:"#ede6d6",
  cream:        "#faf7f2",
  amber:        "#d4870a",
  amberLight:   "#f0a832",
  rust:         "#b84c1a",
  gold:         "#c9a227",
  text:         "#1c1a16",
  textMid:      "#4a4540",
  textLight:    "#8a8278",
  white:        "#fffef9",
  border:       "rgba(28,58,24,0.12)",
  borderGold:   "rgba(201,162,39,0.35)",
};

// ── Default prices ────────────────────────────────────────────
const DEFAULT_PRICES = { starter: 599, family: 1199, wellness: 1999 };

// ── Frequencies ───────────────────────────────────────────────
const FREQUENCIES = [
  { key: "monthly",   label: "Monthly",   discount: 0,  billingNote: "Billed every month"    },
  { key: "quarterly", label: "Quarterly", discount: 8,  billingNote: "Billed every 3 months" },
  { key: "biannual",  label: "Biannual",  discount: 17, billingNote: "Billed every 6 months" },
];

// ── Plans ─────────────────────────────────────────────────────
const PLANS = [
  {
    key: "starter", icon: faWheatAwn,
    accentColor: T.leaf, accentBg: "#e8f5e9",
    name: "Starter", subtitle: "Box", badge: null,
    tagline: "For individuals & couples beginning their millet journey.",
    features: [
      "2 millet varieties (500g each)",
      "1 heritage rice variety (1 kg)",
      "100g jaggery block or powder",
      "Free pan-India delivery",
      "Recipe card with every box",
    ],
    featured: false,
  },
  {
    key: "family", icon: faHouse,
    accentColor: T.forest, accentBg: T.forest,
    name: "Family", subtitle: "Box", badge: "Most Popular",
    tagline: "Curated for households of 3–5. Full everyday grain coverage.",
    features: [
      "4 millet varieties (500g each)",
      "2 heritage rice varieties (1 kg each)",
      "500g jaggery (palm or sugarcane)",
      "1 cold-pressed oil (500 ml)",
      "Free priority delivery",
      "Seasonal recipe booklet",
      "5% loyalty credit on each delivery",
      "Swap products anytime",
    ],
    featured: true,
  },
  {
    key: "wellness", icon: faHeartPulse,
    accentColor: T.amber, accentBg: "#fff8e6",
    name: "Wellness", subtitle: "Box", badge: "Premium",
    tagline: "For health-first households. The complete organic pantry.",
    features: [
      "6 millet varieties (500g each)",
      "3 heritage rice varieties (1 kg each)",
      "1 kg jaggery assortment",
      "2 cold-pressed oils (500 ml each)",
      "Pulses & millet flour selection",
      "Honey or nuts add-on included",
      "Priority + insulated delivery",
      "10% loyalty credit on each delivery",
      "Farm updates newsletter",
      "Custom product requests",
    ],
    featured: false,
  },
];

// ── Product boxes ─────────────────────────────────────────────
const PRODUCT_BOXES = [
  {
    icon: faWheatAwn, tag: "Millets",
    color: { bg: "#e8f5e9", text: T.forestMid, icon: T.leaf },
    name: "Millet Rotation Pack",
    desc: "A rotating monthly selection from our full millet range.",
    items: ["Foxtail","Little Millet","Kodo","Barnyard","Finger / Ragi","Pearl","Proso"],
  },
  {
    icon: faBowlFood, tag: "Heritage Rice",
    color: { bg: "#e0f2f1", text: "#00695c", icon: "#00897b" },
    name: "Heritage Rice Collection",
    desc: "Traditional varieties stone-milled and unpolished for maximum nutrition.",
    items: ["Seeraga Samba","Kuthiraivali","Red Rice","Black Rice","Mappillai Samba"],
  },
  {
    icon: faJar, tag: "Jaggery",
    color: { bg: "#fff8e1", text: T.rust, icon: T.amber },
    name: "Natural Sweetener Bundle",
    desc: "Sulphate-free jaggery from traditional mills — no chemicals, no compromise.",
    items: ["Palm Jaggery (Karupatti)","Sugarcane Block","Powdered Jaggery"],
  },
  {
    icon: faPlusCircle, tag: "Add-ons",
    color: { bg: "#fce4ec", text: "#b71c1c", icon: "#e53935" },
    name: "Pantry Staples Top-Up",
    desc: "Complete your organic pantry — add to any plan at checkout.",
    items: ["Groundnut Oil","Sesame Oil","Toor Dal","Green Gram","Millet Flour","Honey"],
  },
];

// ── Compare rows ──────────────────────────────────────────────
const COMPARE_ROWS = [
  { label: "Millet varieties",  starter: "2 types",      family: "4 types",        wellness: "6 types"              },
  { label: "Heritage rice",     starter: "1 kg",         family: "2 kg",           wellness: "3 kg"                 },
  { label: "Jaggery",           starter: "100 g",        family: "500 g",          wellness: "1 kg assortment"      },
  { label: "Cold-pressed oil",  starter: "—",            family: "1 bottle",       wellness: "2 bottles"            },
  { label: "Pulses & flour",    starter: "—",            family: "—",              wellness: "Included"             },
  { label: "Loyalty credits",   starter: "—",            family: "5% / delivery",  wellness: "10% / delivery"       },
  { label: "Recipe content",    starter: "Card",         family: "Booklet",        wellness: "Booklet + newsletter" },
  { label: "Product swaps",     starter: "Once/quarter", family: "Anytime",        wellness: "Anytime + custom"     },
  { label: "Delivery",          starter: "Standard",     family: "Priority",       wellness: "Priority + insulated" },
];

// ── FAQs ──────────────────────────────────────────────────────
const FAQS = [
  { q: "Can I skip or pause a delivery?",
    a: "Yes — log into your account and skip, pause, or reschedule your next delivery anytime. Changes must be made at least 3 days before dispatch." },
  { q: "Can I customise which products I receive?",
    a: "Family and Wellness subscribers can swap individual products anytime from our catalogue. Starter subscribers may swap once per quarter." },
  { q: "Is there a minimum subscription period?",
    a: "No lock-in whatsoever. Cancel before any billing cycle without fees. Quarterly and biannual plans are billed upfront but can be paused after each delivery." },
  { q: "How are products packaged and shipped?",
    a: "All products are packed in food-grade, resealable pouches. Wellness subscribers receive insulated outer packaging. We deliver across all pin codes in India." },
  { q: "How do loyalty credits work?",
    a: "Family subscribers earn 5% and Wellness subscribers earn 10% credit on each delivered order. Credits accumulate in your account for future orders." },
  { q: "Can I gift a subscription box?",
    a: "Absolutely! Gift subscriptions are available for all three plans for 1, 3, or 6 months with a personalised message shipped directly to the recipient." },
];

// ── Perks ─────────────────────────────────────────────────────
const PERKS = [
  { icon: faSeedling, num: "18%",     label: "Max savings",        desc: "On biannual plans" },
  { icon: faRotate,   num: "Zero",    label: "Lock-in or fees",    desc: "Cancel anytime"    },
  { icon: faTruck,    num: "Free",    label: "Pan-India delivery", desc: "Every order"       },
  { icon: faAward,    num: "10%",     label: "Loyalty credit",     desc: "Wellness plan"     },
  { icon: faLeaf,     num: "100%",    label: "Organic",            desc: "Certified farms"   },
  { icon: faGear,     num: "Anytime", label: "Swap products",      desc: "Family & Wellness" },
];

// ── Helpers ───────────────────────────────────────────────────
function calcPrice(basePrice, freqKey) {
  const freq = FREQUENCIES.find(f => f.key === freqKey);
  if (!freq || freq.discount === 0) return basePrice;
  return Math.round(basePrice * (1 - freq.discount / 100));
}
function calcSaving(basePrice, freqKey) {
  const freq = FREQUENCIES.find(f => f.key === freqKey);
  if (!freq || freq.discount === 0) return null;
  const months = freqKey === "quarterly" ? 3 : 6;
  return (basePrice - calcPrice(basePrice, freqKey)) * months;
}

// ── Global CSS ────────────────────────────────────────────────
const GLOBAL_CSS = `
  ${FONT_LINK}
  .sub-page * { box-sizing: border-box; }
  .sub-page { font-family: 'DM Sans', sans-serif; }
  .dp { font-family: 'Playfair Display', Georgia, serif; }
  .plan-card { transition: transform 0.22s cubic-bezier(.34,1.56,.64,1), box-shadow 0.22s ease; cursor: pointer; }
  .plan-card:hover { transform: translateY(-4px); }
  .plan-card-dark:hover { transform: translateY(-6px); box-shadow: 0 20px 56px rgba(28,58,24,0.4) !important; }
  .tab-btn { transition: all 0.18s ease; }
  .freq-btn { transition: all 0.2s ease; }
  .faq-row:hover { background: ${T.parchment} !important; }
  .perk-card { transition: transform 0.18s ease, box-shadow 0.18s ease; }
  .perk-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(28,58,24,0.1); }
  .pill-btn { transition: transform 0.15s ease, box-shadow 0.15s ease; }
  .pill-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(28,58,24,0.25); }
  @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .fu  { animation: fadeUp 0.4s ease both; }
  .fu1 { animation-delay:0.04s; }
  .fu2 { animation-delay:0.11s; }
  .fu3 { animation-delay:0.18s; }
  .no-scrollbar::-webkit-scrollbar { display:none; }
`;

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════

function FrequencyToggle({ selected, onChange }) {
  return (
    <div style={{
      display: "flex", gap: 4,
      background: T.parchmentDark,
      borderRadius: 14, padding: 4,
      border: `1px solid ${T.borderGold}`,
      marginBottom: 24,
    }}>
      {FREQUENCIES.map(f => (
        <button key={f.key} className="freq-btn" onClick={() => onChange(f.key)} style={{
          flex: 1,
          background: selected === f.key
            ? `linear-gradient(135deg, ${T.forest}, ${T.forestMid})`
            : "transparent",
          color: selected === f.key ? T.white : T.textMid,
          border: "none", borderRadius: 10, padding: "10px 6px",
          fontFamily: "'DM Sans',sans-serif", fontWeight: selected === f.key ? 700 : 500,
          fontSize: 12, cursor: "pointer",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
          boxShadow: selected === f.key ? "0 2px 12px rgba(28,58,24,0.28)" : "none",
        }}>
          <span>{f.label}</span>
          {f.discount > 0 && (
            <span style={{
              background: selected === f.key ? T.gold : "#fff3d4",
              color: selected === f.key ? T.forest : T.amber,
              borderRadius: 6, padding: "1px 7px",
              fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
            }}>
              SAVE {f.discount}%
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function PlanCard({ plan, freq, onSelect, user, navigate, index }) {
  const price   = calcPrice(plan.basePrice, freq);
  const saving  = calcSaving(plan.basePrice, freq);
  const freqObj = FREQUENCIES.find(f => f.key === freq);

  // ✅ FIX 1: scroll to top when redirecting to sign-in
  const handleClick = () => {
    if (!user) { navigate("/signin"); window.scrollTo(0, 0); return; }
    onSelect(plan);
  };

  if (plan.featured) {
    return (
      <div className={`plan-card plan-card-dark fu fu${index+1}`} onClick={handleClick} style={{
        background: `linear-gradient(160deg, ${T.forest} 0%, #1a4516 50%, #0f2b0c 100%)`,
        borderRadius: 20, padding: "26px 22px",
        position: "relative", overflow: "hidden",
        boxShadow: "0 16px 48px rgba(28,58,24,0.35)",
        border: "1px solid rgba(201,162,39,0.25)",
      }}>
        {/* Dot texture */}
        <div style={{
          position:"absolute",inset:0,opacity:0.035,borderRadius:20,
          backgroundImage:"radial-gradient(circle, #fff 1px, transparent 1px)",
          backgroundSize:"20px 20px",
        }}/>
        {/* Glow */}
        <div style={{
          position:"absolute",top:-40,right:-40,width:160,height:160,
          background:"radial-gradient(circle, rgba(201,162,39,0.15), transparent 65%)",
        }}/>

        {/* Badge */}
        <div style={{
          display:"inline-flex",alignItems:"center",gap:5,
          background:`linear-gradient(135deg,${T.gold},${T.amberLight})`,
          borderRadius:20,padding:"4px 12px",
          fontSize:9,fontWeight:800,letterSpacing:0.8,textTransform:"uppercase",
          color:T.forest,marginBottom:16,
        }}>
          <FontAwesomeIcon icon={faStar} style={{fontSize:8}}/> Most Popular
        </div>

        {/* Header row */}
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:18}}>
          <div>
            <div className="dp" style={{fontSize:30,fontWeight:700,color:T.white,lineHeight:1}}>{plan.name}</div>
            <div className="dp" style={{fontSize:30,fontWeight:700,color:"rgba(255,255,255,0.3)",lineHeight:1,marginTop:1}}>{plan.subtitle}</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.48)",marginTop:7,lineHeight:1.55,maxWidth:190}}>{plan.tagline}</div>
          </div>
          <div style={{
            width:50,height:50,borderRadius:14,flexShrink:0,
            background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.13)",
            display:"flex",alignItems:"center",justifyContent:"center",
          }}>
            <FontAwesomeIcon icon={plan.icon} style={{fontSize:22,color:T.gold}}/>
          </div>
        </div>

        {/* Price block */}
        <div style={{
          background:"rgba(255,255,255,0.06)",borderRadius:14,
          padding:"14px 16px",marginBottom:18,
          border:"1px solid rgba(255,255,255,0.09)",
        }}>
          <div style={{display:"flex",alignItems:"baseline",gap:2}}>
            <span style={{fontSize:15,color:T.gold,fontWeight:700}}>₹</span>
            <span className="dp" style={{fontSize:44,fontWeight:900,color:T.white,lineHeight:1}}>
              {price.toLocaleString("en-IN")}
            </span>
            <span style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginLeft:4}}>/mo</span>
          </div>
          <div style={{fontSize:10,color:"rgba(255,255,255,0.38)",marginTop:3}}>{freqObj.billingNote}</div>
          {saving && (
            <div style={{
              display:"inline-flex",alignItems:"center",gap:4,marginTop:8,
              background:"rgba(201,162,39,0.18)",border:"1px solid rgba(201,162,39,0.3)",
              borderRadius:8,padding:"3px 10px",fontSize:10,fontWeight:700,color:T.gold,
            }}>
              <FontAwesomeIcon icon={faFire} style={{fontSize:8}}/> Save ₹{saving.toLocaleString("en-IN")} total
            </div>
          )}
        </div>

        {/* Features */}
        <div style={{marginBottom:20}}>
          {plan.features.map((f,i) => (
            <div key={i} style={{
              display:"flex",alignItems:"center",gap:10,
              paddingBottom:9,marginBottom:9,
              borderBottom: i<plan.features.length-1 ? "1px solid rgba(255,255,255,0.07)" : "none",
              fontSize:12,color:"rgba(255,255,255,0.76)",
            }}>
              <div style={{
                width:17,height:17,borderRadius:"50%",flexShrink:0,
                background:"rgba(201,162,39,0.18)",border:"1px solid rgba(201,162,39,0.38)",
                display:"flex",alignItems:"center",justifyContent:"center",
              }}>
                <FontAwesomeIcon icon={faCheck} style={{fontSize:7,color:T.gold}}/>
              </div>
              {f}
            </div>
          ))}
        </div>

        <button style={{
          width:"100%",padding:"14px",
          background:`linear-gradient(135deg,${T.gold},${T.amberLight})`,
          border:"none",borderRadius:12,cursor:"pointer",
          fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:13,color:T.forest,
          display:"flex",alignItems:"center",justifyContent:"center",gap:7,letterSpacing:0.3,
        }}>
          Choose {plan.name} Box <FontAwesomeIcon icon={faArrowRight} style={{fontSize:11}}/>
        </button>
      </div>
    );
  }

  // Standard card
  return (
    <div className={`plan-card fu fu${index+1}`} onClick={handleClick} style={{
      background: T.white, borderRadius: 20, padding: "22px 20px",
      position: "relative",
      boxShadow: "0 4px 20px rgba(28,58,24,0.07)",
      border: `1px solid ${T.border}`,
    }}>
      {plan.badge && (
        <div style={{
          position:"absolute",top:16,right:16,
          background: plan.key==="wellness"
            ? `linear-gradient(135deg,${T.amber},${T.amberLight})`
            : T.parchment,
          border:`1px solid ${plan.key==="wellness"?"transparent":T.borderGold}`,
          borderRadius:8,padding:"3px 10px",
          fontSize:9,fontWeight:800,letterSpacing:0.5,textTransform:"uppercase",
          color: plan.key==="wellness"?"#fff":T.amber,
          display:"flex",alignItems:"center",gap:4,
        }}>
          {plan.key==="wellness" && <FontAwesomeIcon icon={faCrown} style={{fontSize:8}}/>}
          {plan.badge}
        </div>
      )}

      <div style={{
        width:52,height:52,borderRadius:16,
        background: plan.key==="wellness" ? "#fff8e6" : T.parchment,
        border:`1px solid ${T.borderGold}`,
        display:"flex",alignItems:"center",justifyContent:"center",marginBottom:14,
      }}>
        <FontAwesomeIcon icon={plan.icon} style={{fontSize:22,color:plan.accentColor}}/>
      </div>

      <div className="dp" style={{fontSize:24,fontWeight:700,color:T.text,lineHeight:1}}>{plan.name}</div>
      <div className="dp" style={{fontSize:24,fontWeight:700,color:T.textLight,lineHeight:1,marginBottom:7}}>{plan.subtitle}</div>
      <div style={{fontSize:11,color:T.textLight,lineHeight:1.65,marginBottom:14}}>{plan.tagline}</div>

      <div style={{height:1,background:`linear-gradient(90deg,${T.borderGold},transparent)`,marginBottom:14}}/>

      <div style={{marginBottom:16}}>
        <div style={{display:"flex",alignItems:"baseline",gap:2}}>
          <span style={{fontSize:13,color:T.textLight,fontWeight:600}}>₹</span>
          <span className="dp" style={{fontSize:38,fontWeight:900,color:plan.accentColor,lineHeight:1}}>
            {price.toLocaleString("en-IN")}
          </span>
          <span style={{fontSize:11,color:T.textLight,marginLeft:3}}>/month</span>
        </div>
        <div style={{fontSize:10,color:T.textLight,marginTop:3}}>{freqObj.billingNote}</div>
        {saving && (
          <div style={{
            display:"inline-flex",alignItems:"center",gap:4,marginTop:7,
            background: plan.key==="wellness"?"#fff8e6":T.parchment,
            border:`1px solid ${T.borderGold}`,
            borderRadius:8,padding:"3px 10px",fontSize:10,fontWeight:700,color:plan.accentColor,
          }}>
            Save ₹{saving.toLocaleString("en-IN")} total
          </div>
        )}
      </div>

      <div style={{marginBottom:18}}>
        {plan.features.map((f,i) => (
          <div key={i} style={{
            display:"flex",alignItems:"center",gap:9,
            paddingBottom:9,marginBottom:9,
            borderBottom: i<plan.features.length-1 ? `1px solid ${T.border}` : "none",
            fontSize:12,color:T.textMid,
          }}>
            <FontAwesomeIcon icon={faCheck} style={{fontSize:9,color:plan.accentColor,flexShrink:0}}/>
            {f}
          </div>
        ))}
      </div>

      <button style={{
        width:"100%",padding:"13px",
        background: plan.key==="wellness"
          ? `linear-gradient(135deg,${T.amber},${T.amberLight})`
          : `linear-gradient(135deg,${T.forestMid},${T.leaf})`,
        border:"none",borderRadius:12,cursor:"pointer",
        fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:12,color:"#fff",
        display:"flex",alignItems:"center",justifyContent:"center",gap:7,letterSpacing:0.2,
      }}>
        Choose {plan.name} Box <FontAwesomeIcon icon={faArrowRight} style={{fontSize:10}}/>
      </button>
    </div>
  );
}

function ProductBoxCard({ box }) {
  return (
    <div style={{
      background:T.white,border:`1px solid ${T.border}`,
      borderRadius:16,padding:"18px 16px",
      boxShadow:"0 2px 12px rgba(28,58,24,0.05)",
    }}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
        <div style={{width:42,height:42,background:box.color.bg,borderRadius:12,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <FontAwesomeIcon icon={box.icon} style={{fontSize:18,color:box.color.icon}}/>
        </div>
        <div>
          <div style={{display:"inline-block",background:box.color.bg,color:box.color.text,fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:0.5,padding:"2px 9px",borderRadius:6,marginBottom:3}}>{box.tag}</div>
          <div className="dp" style={{fontSize:14,fontWeight:700,color:T.text}}>{box.name}</div>
        </div>
      </div>
      <div style={{fontSize:12,color:T.textLight,marginBottom:12,lineHeight:1.65}}>{box.desc}</div>
      <div style={{display:"flex",flexWrap:"wrap",gap:5}}>
        {box.items.map(item => (
          <span key={item} style={{fontSize:10,fontWeight:600,background:T.parchment,border:`1px solid ${T.border}`,borderRadius:20,padding:"3px 10px",color:T.textMid}}>{item}</span>
        ))}
      </div>
    </div>
  );
}

function CompareTable() {
  return (
    <div style={{overflowX:"auto",borderRadius:16,border:`1px solid ${T.border}`,boxShadow:"0 4px 20px rgba(28,58,24,0.06)"}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead>
          <tr style={{background:`linear-gradient(135deg,${T.forest},${T.forestMid})`}}>
            <th style={{padding:"14px 14px",textAlign:"left",color:"rgba(255,255,255,0.6)",fontWeight:600,fontSize:11,minWidth:130,fontFamily:"'DM Sans',sans-serif"}}>Feature</th>
            <th style={{padding:"14px 10px",textAlign:"center",color:"#fff",fontWeight:700,fontSize:11,fontFamily:"'Playfair Display',serif"}}>Starter</th>
            <th style={{padding:"14px 10px",textAlign:"center",color:T.gold,fontWeight:700,fontSize:11,background:"rgba(0,0,0,0.15)",fontFamily:"'Playfair Display',serif"}}>Family ★</th>
            <th style={{padding:"14px 10px",textAlign:"center",color:"#fff",fontWeight:700,fontSize:11,fontFamily:"'Playfair Display',serif"}}>Wellness</th>
          </tr>
        </thead>
        <tbody>
          {COMPARE_ROWS.map((row,i) => (
            <tr key={row.label} style={{background: i%2===0 ? T.parchment : T.white}}>
              <td style={{padding:"11px 14px",fontWeight:600,color:T.textMid,fontSize:11}}>{row.label}</td>
              <td style={{padding:"11px 10px",textAlign:"center",color:row.starter==="—"?"#d0ccc5":T.textMid,fontSize:11}}>{row.starter}</td>
              <td style={{padding:"11px 10px",textAlign:"center",fontWeight:700,color:T.forestMid,fontSize:11,background:"rgba(45,90,39,0.04)"}}>{row.family}</td>
              <td style={{padding:"11px 10px",textAlign:"center",color:row.wellness==="—"?T.textLight:T.amber,fontWeight:row.wellness==="—"?400:600,fontSize:11}}>{row.wellness}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HowItWorks() {
  const steps = [
    { num:"01", icon:faClipboardList, title:"Choose your plan",   desc:"Pick Starter, Family, or Wellness and set billing frequency." },
    { num:"02", icon:faGear,          title:"Customise your box", desc:"Select varieties, jaggery, rice types and any add-ons."      },
    { num:"03", icon:faBox,           title:"We pack & ship",     desc:"Hand-packed in food-grade pouches, dispatched on your date." },
    { num:"04", icon:faAward,         title:"Earn credits",       desc:"Cook healthy and earn loyalty credits on every delivery."    },
  ];
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
      {steps.map((s,i) => (
        <div key={i} style={{background:T.white,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 14px",position:"relative",overflow:"hidden"}}>
          <div className="dp" style={{position:"absolute",top:6,right:10,fontSize:34,fontWeight:900,color:T.parchmentDark,lineHeight:1,userSelect:"none"}}>{s.num}</div>
          <div style={{width:34,height:34,borderRadius:10,background:T.parchment,border:`1px solid ${T.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:10}}>
            <FontAwesomeIcon icon={s.icon} style={{fontSize:14,color:T.forestMid}}/>
          </div>
          <div style={{fontWeight:700,fontSize:12,color:T.text,marginBottom:4}}>{s.title}</div>
          <div style={{fontSize:11,color:T.textLight,lineHeight:1.55}}>{s.desc}</div>
        </div>
      ))}
    </div>
  );
}

function FaqItem({ faq, last }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{borderBottom: last ? "none" : `1px solid ${T.border}`}}>
      <button
        className="faq-row"
        onClick={() => setOpen(o => !o)}
        style={{
          width:"100%",background: open ? T.parchment : "none",border:"none",
          borderRadius: open ? "10px 10px 0 0" : 0,
          display:"flex",justifyContent:"space-between",alignItems:"center",
          padding:"14px 14px",cursor:"pointer",gap:12,textAlign:"left",
          transition:"background 0.15s",
        }}
      >
        <span style={{fontWeight:600,fontSize:13,color:T.text,flex:1}}>{faq.q}</span>
        <div style={{
          width:26,height:26,borderRadius:"50%",flexShrink:0,
          background: open ? T.forestMid : T.parchmentDark,
          display:"flex",alignItems:"center",justifyContent:"center",
          transition:"all 0.2s",
        }}>
          <FontAwesomeIcon icon={faChevronDown} style={{
            fontSize:10,color: open ? T.white : T.textMid,
            transform: open ? "rotate(180deg)" : "none",
            transition:"transform 0.22s",
          }}/>
        </div>
      </button>
      {open && (
        <div style={{fontSize:12,color:T.textMid,lineHeight:1.8,padding:"0 14px 14px",background:T.parchment,borderRadius:"0 0 10px 10px"}}>
          {faq.a}
        </div>
      )}
    </div>
  );
}

function STitle({ children, sub }) {
  return (
    <div style={{marginBottom:18}}>
      <div className="dp" style={{fontSize:22,fontWeight:700,color:T.text,lineHeight:1.2}}>{children}</div>
      {sub && <div style={{fontSize:12,color:T.textLight,marginTop:5,lineHeight:1.6}}>{sub}</div>}
    </div>
  );
}

function ConfirmModal({ plan, freq, user, onConfirm, onCancel, loading }) {
  const price   = calcPrice(plan.basePrice, freq);
  const freqObj = FREQUENCIES.find(f => f.key === freq);
  const saving  = calcSaving(plan.basePrice, freq);
  return (
    <div
      onClick={e => { if (e.target===e.currentTarget) onCancel(); }}
      style={{
        position:"fixed",inset:0,zIndex:200,
        background:"rgba(28,34,24,0.65)",backdropFilter:"blur(6px)",
        display:"flex",alignItems:"center",justifyContent:"center",
        padding:16,overflowY:"auto",
      }}
    >
      <div style={{
        background:T.cream,borderRadius:24,width:"100%",maxWidth:460,
        boxShadow:"0 24px 80px rgba(0,0,0,0.35)",
        position:"relative",overflow:"hidden",
        maxHeight:"calc(100vh - 32px)",overflowY:"auto",margin:"auto",
      }}>
        <button onClick={onCancel} style={{
          position:"absolute",top:14,right:14,zIndex:1,
          background:"rgba(255,255,255,0.9)",border:"none",borderRadius:"50%",
          width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",
          cursor:"pointer",color:T.textMid,fontSize:13,
        }}>
          <FontAwesomeIcon icon={faXmark}/>
        </button>

        <div style={{
          background: plan.featured
            ? `linear-gradient(160deg,${T.forest},#0f2b0c)`
            : T.parchmentDark,
          padding:"32px 24px 24px",textAlign:"center",
          borderBottom:`1px solid ${T.borderGold}`,
        }}>
          <div style={{
            width:64,height:64,borderRadius:18,margin:"0 auto 14px",
            background: plan.featured ? "rgba(255,255,255,0.1)" : T.white,
            border:`1px solid ${T.borderGold}`,
            display:"flex",alignItems:"center",justifyContent:"center",
          }}>
            <FontAwesomeIcon icon={plan.icon} style={{fontSize:26,color: plan.featured ? T.gold : plan.accentColor}}/>
          </div>
          <div className="dp" style={{fontSize:26,fontWeight:700,color: plan.featured ? T.white : T.text}}>
            {plan.name} {plan.subtitle}
          </div>
          <div style={{fontSize:12,color: plan.featured ? "rgba(255,255,255,0.5)" : T.textLight,marginTop:6}}>{plan.tagline}</div>
        </div>

        <div style={{padding:"20px 20px 0"}}>
          <div style={{background:T.white,border:`1px solid ${T.border}`,borderRadius:14,padding:"14px 16px",marginBottom:14}}>
            {[{label:"Plan",value:`${plan.name} ${plan.subtitle}`},{label:"Frequency",value:freqObj.label},{label:"Billing",value:freqObj.billingNote}].map(row => (
              <div key={row.label} style={{display:"flex",justifyContent:"space-between",fontSize:12,paddingBottom:7,marginBottom:7,borderBottom:`1px solid ${T.border}`}}>
                <span style={{color:T.textLight}}>{row.label}</span>
                <span style={{fontWeight:600,color:T.text}}>{row.value}</span>
              </div>
            ))}
            <div style={{display:"flex",justifyContent:"space-between",paddingTop:4}}>
              <span style={{color:T.textLight,fontSize:12}}>Price</span>
              <span className="dp" style={{fontSize:18,fontWeight:700,color:plan.accentColor}}>
                ₹{price.toLocaleString("en-IN")}<span style={{fontSize:12,fontWeight:400,color:T.textLight}}>/month</span>
              </span>
            </div>
            {saving && (
              <div style={{marginTop:8,background:T.parchment,border:`1px solid ${T.borderGold}`,borderRadius:8,padding:"5px 10px",fontSize:11,fontWeight:700,color:T.amber,textAlign:"center"}}>
                You save ₹{saving.toLocaleString("en-IN")} total
              </div>
            )}
          </div>

          {user && (
            <div style={{background:"#f0fdf4",border:"1px solid #a5d6a7",borderRadius:10,padding:"9px 12px",fontSize:12,color:T.forestMid,marginBottom:14,display:"flex",alignItems:"center",gap:7}}>
              <FontAwesomeIcon icon={faCheckCircle} style={{color:"#4caf50"}}/>
              Subscribing as <strong>{user.name||user.email}</strong>
            </div>
          )}

          <div style={{background:"#fff8ec",border:`1px solid ${T.borderGold}`,borderRadius:10,padding:"10px 13px",fontSize:11,color:T.amber,lineHeight:1.75,marginBottom:16}}>
            <div style={{fontWeight:700,marginBottom:3,display:"flex",alignItems:"center",gap:6}}>
              <FontAwesomeIcon icon={faCircleInfo}/> What happens next?
            </div>
            Our team will reach out on WhatsApp to confirm your selections and arrange the first delivery.
          </div>

          <button onClick={onConfirm} style={{
            width:"100%",padding:"14px",
            background:`linear-gradient(135deg,${T.forest},${T.forestMid})`,
            border:"none",borderRadius:12,cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:13,color:"#fff",
            display:"flex",alignItems:"center",justifyContent:"center",gap:8,
            marginBottom:10,opacity: loading ? 0.7 : 1,
          }}>
            {loading
              ? <><FontAwesomeIcon icon={faRepeat} spin/> Processing...</>
              : <><FontAwesomeIcon icon={faComments}/> Confirm on WhatsApp</>}
          </button>
          <button onClick={onCancel} style={{
            width:"100%",padding:"11px",background:"transparent",
            border:`1px solid ${T.border}`,borderRadius:12,cursor:"pointer",
            fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:12,color:T.textMid,marginBottom:20,
          }}>
            ← Go Back
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════
export default function SubscriptionPage({ auth }) {
  const navigate = useNavigate();
  const { user } = auth || {};
  const [freq,         setFreq]         = useState("monthly");
  const [selected,     setSelected]     = useState(null);
  const [toast,        setToast]        = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [activeTab,    setActiveTab]    = useState("plans");
  const [livePrices,   setLivePrices]   = useState(DEFAULT_PRICES);
  const [pricesLoaded, setPricesLoaded] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    supabase.from("subscription_prices").select("plan, price")
      .then(({ data }) => {
        if (data?.length > 0) {
          const map = { ...DEFAULT_PRICES };
          data.forEach(r => { map[r.plan] = r.price; });
          setLivePrices(map);
        }
        setPricesLoaded(true);
      })
      .catch(() => setPricesLoaded(true));
  }, []);

  const plansWithPrices = PLANS.map(p => ({ ...p, basePrice: livePrices[p.key] || DEFAULT_PRICES[p.key] }));

  async function handleConfirm() {
    if (submitting.current || !selected) return;
    submitting.current = true; setSaving(true);
    const price   = calcPrice(selected.basePrice, freq);
    const freqObj = FREQUENCIES.find(f => f.key === freq);
    const savingAmt = calcSaving(selected.basePrice, freq);
    try {
      if (user) {
        await supabase.from("subscriptions").insert({
          user_id:user.id, user_name:user.name||null,
          user_email:user.email||null, user_phone:user.phone||null,
          plan:selected.key, frequency:freq, price,
          status:"pending", created_at:new Date().toISOString(),
        });
      }
      const savingStr = savingAmt ? `\n💰 Saving: ₹${savingAmt.toLocaleString("en-IN")} total` : "";
      const msg = encodeURIComponent(
        `Hi RK Farm Foods! 🌾\n\nI'd like to subscribe to:\n\n` +
        `📦 Plan: ${selected.name} ${selected.subtitle}\n` +
        `🗓 Frequency: ${freqObj.label}\n` +
        `💵 Price: ₹${price.toLocaleString("en-IN")}/month${savingStr}\n\n` +
        `👤 Name: ${user?.name||"Customer"}\n` +
        (user?.phone ? `📱 Phone: ${user.phone}\n` : "") +
        `\nPlease confirm my subscription. Thank you! 🙏`
      );
      window.open(`https://wa.me/${config.whatsappNumber}?text=${msg}`, "_blank");
      setSelected(null);
      setToast("Subscription request sent on WhatsApp!");
    } catch (err) {
      console.error(err);
      setToast("Something went wrong. Please try WhatsApp directly.");
    } finally {
      setSaving(false); submitting.current = false;
    }
  }

  const tabs = [
    { key:"plans",        icon:faClipboardList,  label:"Plans"         },
    { key:"whats-inside", icon:faBox,            label:"What's Inside" },
    { key:"compare",      icon:faUsers,          label:"Compare"       },
    { key:"faq",          icon:faQuestionCircle, label:"FAQ"           },
  ];

  return (
    <div className="sub-page" style={{minHeight:"100vh",background:T.cream}}>
      <style>{GLOBAL_CSS}</style>

      {/* ── HERO ── */}
      <div style={{
        background:`linear-gradient(160deg,${T.forest} 0%,#1e4819 45%,#0d2609 100%)`,
        padding:"44px 20px 52px",position:"relative",overflow:"hidden",textAlign:"center",
      }}>
        {/* Grain */}
        <div style={{position:"absolute",inset:0,opacity:0.35,backgroundImage:"url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E\")"}}/>
        {/* Glow */}
        <div style={{position:"absolute",top:"10%",left:"50%",transform:"translateX(-50%)",width:500,height:300,background:"radial-gradient(ellipse, rgba(201,162,39,0.1) 0%, transparent 65%)",pointerEvents:"none"}}/>

        <div style={{position:"relative"}}>
          <div style={{
            display:"inline-flex",alignItems:"center",gap:6,
            background:"rgba(201,162,39,0.14)",border:"1px solid rgba(201,162,39,0.28)",
            borderRadius:20,padding:"4px 14px",
            fontSize:9,fontWeight:700,letterSpacing:1.2,textTransform:"uppercase",
            color:T.gold,marginBottom:18,
          }}>
            <FontAwesomeIcon icon={faWheatAwn} style={{fontSize:9}}/> RK Farm Foods — Organic Subscription
          </div>

          <h1 className="dp" style={{
            fontSize:"clamp(30px,7vw,52px)",fontWeight:700,
            color:T.white,lineHeight:1.15,marginBottom:4,letterSpacing:"-0.01em",
          }}>
            Farm-to-Table,
          </h1>
          <h1 className="dp" style={{
            fontSize:"clamp(30px,7vw,52px)",fontWeight:400,fontStyle:"italic",
            color:T.gold,lineHeight:1.15,marginBottom:18,
          }}>
            Every Month.
          </h1>

          <p style={{fontSize:"clamp(12px,3vw,14px)",color:"rgba(255,255,255,0.58)",lineHeight:1.75,maxWidth:340,margin:"0 auto 26px"}}>
            Never run out of organic millets, heritage rice & natural jaggery.
            Subscribe, save up to 18%, and eat better every single month.
          </p>

          <div style={{display:"flex",justifyContent:"center",flexWrap:"wrap",gap:8}}>
            {[
              {icon:faLeaf,   text:"100% Organic"},
              {icon:faTruck,  text:"Free Delivery"},
              {icon:faRotate, text:"Cancel Anytime"},
              {icon:faSeedling,text:"Save up to 18%"},
            ].map(b => (
              <div key={b.text} style={{
                display:"inline-flex",alignItems:"center",gap:6,
                background:"rgba(255,255,255,0.07)",border:"1px solid rgba(255,255,255,0.11)",
                borderRadius:20,padding:"5px 13px",fontSize:10,fontWeight:600,color:"rgba(255,255,255,0.72)",
              }}>
                <FontAwesomeIcon icon={b.icon} style={{fontSize:9,color:T.gold}}/>{b.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div style={{
        background:T.parchmentDark,borderBottom:`1px solid ${T.borderGold}`,
        display:"grid",gridTemplateColumns:"repeat(4,1fr)",padding:"14px 8px",
      }}>
        {[
          {n:"3",   l:"Plans",      icon:faBox},
          {n:"18%", l:"Max Savings",icon:faSeedling},
          {n:"Free",l:"Delivery",   icon:faTruck},
          {n:"0",   l:"Lock-in",    icon:faShieldHalved},
        ].map(({n,l,icon}) => (
          <div key={l} style={{textAlign:"center"}}>
            <FontAwesomeIcon icon={icon} style={{fontSize:11,color:T.amber,display:"block",margin:"0 auto 3px"}}/>
            <div className="dp" style={{fontSize:"clamp(14px,4vw,20px)",fontWeight:700,color:T.forest}}>{n}</div>
            <div style={{fontSize:"clamp(7px,1.8vw,9px)",color:T.textLight,letterSpacing:0.3,fontWeight:600,textTransform:"uppercase"}}>{l}</div>
          </div>
        ))}
      </div>

      {/* ── TABS ── */}
      <div className="no-scrollbar" style={{
        background:T.white,borderBottom:`1px solid ${T.border}`,
        padding:"0 16px",display:"flex",gap:0,overflowX:"auto",
      }}>
        {tabs.map(t => (
          <button key={t.key} className="tab-btn" onClick={() => setActiveTab(t.key)} style={{
            flexShrink:0,background:"none",border:"none",
            borderBottom: activeTab===t.key ? `2.5px solid ${T.forestMid}` : "2.5px solid transparent",
            padding:"14px 16px",fontSize:11,
            fontWeight: activeTab===t.key ? 700 : 500,
            color: activeTab===t.key ? T.forest : T.textLight,
            cursor:"pointer",whiteSpace:"nowrap",
            display:"inline-flex",alignItems:"center",gap:6,
            fontFamily:"'DM Sans',sans-serif",
          }}>
            <FontAwesomeIcon icon={t.icon} style={{fontSize:10}}/>{t.label}
          </button>
        ))}
      </div>

      {/* ── CONTENT ── */}
      <div style={{padding:"24px 16px 16px",maxWidth:560,margin:"0 auto"}}>

        {/* ════ PLANS ════ */}
        {activeTab==="plans" && (
          <>
            <FrequencyToggle selected={freq} onChange={setFreq}/>

            {/* ✅ FIX 2: sign-in banner — scroll to top on navigate */}
            {!user && (
              <div onClick={() => { navigate("/signin"); window.scrollTo(0, 0); }} style={{
                background:T.white,border:`1px solid ${T.borderGold}`,
                borderLeft:`4px solid ${T.amber}`,
                borderRadius:12,padding:"14px 16px",marginBottom:20,
                display:"flex",alignItems:"center",gap:12,cursor:"pointer",
              }}>
                <div style={{width:38,height:38,borderRadius:10,flexShrink:0,background:"#fff8e6",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <FontAwesomeIcon icon={faKey} style={{fontSize:15,color:T.amber}}/>
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:T.text}}>Sign in to subscribe</div>
                  <div style={{fontSize:11,color:T.textLight,marginTop:2}}>Tap any plan to sign in and start your organic journey.</div>
                </div>
                <FontAwesomeIcon icon={faArrowRight} style={{marginLeft:"auto",color:T.amber,fontSize:13}}/>
              </div>
            )}

            {!pricesLoaded ? (
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {[1,2,3].map(i => (
                  <div key={i} style={{height:280,borderRadius:20,background:"linear-gradient(90deg,#ede6d6 25%,#e5dcc8 50%,#ede6d6 75%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s infinite"}}/>
                ))}
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:16}}>
                {plansWithPrices.map((plan,i) => (
                  <PlanCard key={plan.key} plan={plan} freq={freq} index={i} onSelect={setSelected} user={user} navigate={navigate}/>
                ))}
              </div>
            )}
          </>
        )}

        {/* ════ WHAT'S INSIDE ════ */}
        {activeTab==="whats-inside" && (
          <>
            <STitle sub="All products from certified organic farms. Family & Wellness subscribers can swap varieties each cycle.">
              What's in every box?
            </STitle>
            <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:28}}>
              {PRODUCT_BOXES.map(box => <ProductBoxCard key={box.name} box={box}/>)}
            </div>

            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
              <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${T.borderGold})`}}/>
              <span className="dp" style={{fontSize:12,color:T.textLight,fontStyle:"italic",whiteSpace:"nowrap"}}>Why subscribe</span>
              <div style={{flex:1,height:1,background:`linear-gradient(90deg,${T.borderGold},transparent)`}}/>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:10,marginBottom:28}}>
              {PERKS.map(p => (
                <div key={p.label} className="perk-card" style={{background:T.white,border:`1px solid ${T.border}`,borderRadius:14,padding:"16px 14px"}}>
                  <div style={{width:36,height:36,borderRadius:10,background:T.parchment,border:`1px solid ${T.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:10}}>
                    <FontAwesomeIcon icon={p.icon} style={{fontSize:15,color:T.forestMid}}/>
                  </div>
                  <div className="dp" style={{fontSize:21,fontWeight:700,color:T.forestMid}}>{p.num}</div>
                  <div style={{fontSize:10,fontWeight:700,color:T.textMid,textTransform:"uppercase",letterSpacing:0.5,marginTop:2}}>{p.label}</div>
                  <div style={{fontSize:10,color:T.textLight,marginTop:3}}>{p.desc}</div>
                </div>
              ))}
            </div>

            <STitle>How it works</STitle>
            <HowItWorks/>
          </>
        )}

        {/* ════ COMPARE ════ */}
        {activeTab==="compare" && (
          <>
            <STitle sub="Compare plans to find the right fit for your household.">Side by side</STitle>
            <CompareTable/>
            <div style={{marginTop:24}}>
              <STitle>Price at a glance</STitle>
              <FrequencyToggle selected={freq} onChange={setFreq}/>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                {plansWithPrices.map(plan => {
                  const price  = calcPrice(plan.basePrice,freq);
                  const saving = calcSaving(plan.basePrice,freq);
                  return (
                    <div key={plan.key} onClick={() => {
                      // ✅ FIX 3: compare tab price card — scroll to top on sign-in redirect
                      if (!user) { navigate("/signin"); window.scrollTo(0, 0); return; }
                      setActiveTab("plans"); setSelected(plan);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }} style={{
                      background: plan.featured ? `linear-gradient(160deg,${T.forest},#0f2b0c)` : T.white,
                      border:`1.5px solid ${plan.featured?"transparent":T.border}`,
                      borderRadius:14,padding:"16px 10px",textAlign:"center",cursor:"pointer",
                      boxShadow: plan.featured ? "0 8px 28px rgba(28,58,24,0.25)" : "0 2px 10px rgba(28,58,24,0.05)",
                    }}>
                      <div style={{width:38,height:38,borderRadius:10,margin:"0 auto 8px",background: plan.featured?"rgba(255,255,255,0.1)":T.parchment,border:`1px solid ${T.borderGold}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                        <FontAwesomeIcon icon={plan.icon} style={{fontSize:16,color: plan.featured?T.gold:plan.accentColor}}/>
                      </div>
                      <div className="dp" style={{fontSize:11,fontWeight:700,color: plan.featured?"rgba(255,255,255,0.65)":T.textMid,marginBottom:4}}>{plan.name}</div>
                      <div className="dp" style={{fontSize:22,fontWeight:700,color: plan.featured?T.white:plan.accentColor}}>₹{price.toLocaleString("en-IN")}</div>
                      <div style={{fontSize:9,color: plan.featured?"rgba(255,255,255,0.4)":T.textLight,marginBottom:4}}>/month</div>
                      {saving && (
                        <div style={{fontSize:9,fontWeight:700,background: plan.featured?"rgba(201,162,39,0.2)":T.parchment,color: plan.featured?T.gold:T.amber,border:`1px solid ${T.borderGold}`,borderRadius:8,padding:"2px 7px",display:"inline-block"}}>
                          Save ₹{saving.toLocaleString("en-IN")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* ════ FAQ ════ */}
        {activeTab==="faq" && (
          <>
            <STitle>Frequently asked</STitle>
            <div style={{background:T.white,border:`1px solid ${T.border}`,borderRadius:16,overflow:"hidden",boxShadow:"0 4px 16px rgba(28,58,24,0.06)",marginBottom:16}}>
              {FAQS.map((faq,i) => <FaqItem key={i} faq={faq} last={i===FAQS.length-1}/>)}
            </div>

            <div style={{background:`linear-gradient(135deg,${T.forest},#0f2b0c)`,border:"1px solid rgba(201,162,39,0.2)",borderRadius:16,padding:"22px 18px"}}>
              <div className="dp" style={{fontSize:20,fontWeight:700,color:T.white,marginBottom:7}}>Still have questions?</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.52)",marginBottom:16,lineHeight:1.65}}>
                Chat with our team — we'll help you pick the right plan for your family.
              </div>
              <button className="pill-btn" onClick={() => window.open(`https://wa.me/${config.whatsappNumber}?text=Hi! I have a question about your subscription plans.`,"_blank")} style={{
                width:"100%",padding:"13px",
                background:`linear-gradient(135deg,${T.gold},${T.amberLight})`,
                border:"none",borderRadius:12,cursor:"pointer",
                fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:13,color:T.forest,
                display:"flex",alignItems:"center",justifyContent:"center",gap:7,
              }}>
                <FontAwesomeIcon icon={faComments}/> Chat on WhatsApp
              </button>
            </div>
          </>
        )}

        {/* ── BOTTOM CTA ── */}
        <div style={{
          marginTop:28,background:T.parchmentDark,
          border:`1px solid ${T.borderGold}`,
          borderRadius:20,padding:"26px 20px",textAlign:"center",
          position:"relative",overflow:"hidden",
        }}>
          <div style={{position:"absolute",top:-30,right:-30,width:120,height:120,background:"radial-gradient(circle, rgba(201,162,39,0.12), transparent 70%)"}}/>
          <FontAwesomeIcon icon={faWheatAwn} style={{fontSize:30,color:T.amber,marginBottom:12,display:"block"}}/>
          <div className="dp" style={{fontSize:22,fontWeight:700,color:T.text,marginBottom:7}}>Ready to start your organic journey?</div>
          <div style={{fontSize:12,color:T.textLight,marginBottom:18,lineHeight:1.75}}>
            Join hundreds of families across India getting farm-fresh goodness every month.
          </div>
          {/* ✅ FIX 4: bottom CTA — scroll to top on sign-in redirect */}
          <button className="pill-btn" onClick={() => {
            if (!user) { navigate("/signin"); window.scrollTo(0, 0); return; }
            setActiveTab("plans");
            setTimeout(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, 100);
          }} style={{
            background:`linear-gradient(135deg,${T.forest},${T.forestMid})`,
            color:"#fff",border:"none",borderRadius:13,padding:"13px 28px",
            fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:13,cursor:"pointer",
            display:"inline-flex",alignItems:"center",gap:8,
            boxShadow:"0 4px 16px rgba(28,58,24,0.22)",
          }}>
            <FontAwesomeIcon icon={faClipboardList} style={{fontSize:11}}/>
            View All Plans
            <FontAwesomeIcon icon={faArrowRight} style={{fontSize:11}}/>
          </button>
        </div>

      </div>

      {selected && (
        <ConfirmModal plan={selected} freq={freq} user={user} loading={saving} onConfirm={handleConfirm} onCancel={() => setSelected(null)}/>
      )}
      {toast && <Toast message={toast} onDone={() => setToast(null)}/>}
    </div>
  );
}
